import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Layout from '../components/layout/Layout'
import { useAuth } from '../hooks/useAuth'
import { getLicenses, createLicense, type CustomerLicenses, type LicenseItem } from '../lib/api'
import './DashboardPage.css'

function DashboardPage() {
  const { t } = useTranslation()
  const { isAuthenticated, accessToken, loading: authLoading, login } = useAuth()
  const [customers, setCustomers] = useState<CustomerLicenses[]>([])
  const [fetchError, setFetchError] = useState<string | null>(null)
  const [fetchLoading, setFetchLoading] = useState(false)
  const [addingFor, setAddingFor] = useState<string | null>(null)
  const [copiedId, setCopiedId] = useState<string | null>(null)

  useEffect(() => {
    if (!isAuthenticated || !accessToken) return
    setFetchLoading(true)
    getLicenses(accessToken).then(result => {
      setFetchLoading(false)
      if (result.ok) {
        setCustomers(result.data.customers)
      } else if (result.status === 404) {
        setCustomers([])
      } else {
        setFetchError(result.error)
      }
    })
  }, [isAuthenticated, accessToken])

  const handleCopy = async (licenseKey: string, licenseId: string) => {
    await navigator.clipboard.writeText(licenseKey)
    setCopiedId(licenseId)
    setTimeout(() => setCopiedId(null), 2000)
  }

  const handleAddDeployment = async (customerId: string) => {
    if (!accessToken) return
    setAddingFor(customerId)
    const result = await createLicense(customerId, accessToken)
    setAddingFor(null)
    if (result.ok) {
      const newLicense: LicenseItem = {
        licenseId: result.data.licenseId,
        status: result.data.status,
        licenseKey: result.data.licenseKey,
        issuedAt: result.data.issuedAt
      }
      setCustomers(prev =>
        prev.map(c => (c.customerId === customerId ? { ...c, licenses: [...c.licenses, newLicense] } : c))
      )
    } else {
      setFetchError(result.error)
    }
  }

  if (authLoading) {
    return (
      <Layout>
        <div className='dashboard'>
          <p className='dashboard-loading'>{t('dashboard.loading')}</p>
        </div>
      </Layout>
    )
  }

  if (!isAuthenticated) {
    return (
      <Layout>
        <div className='dashboard'>
          <div className='dashboard-unauthenticated'>
            <h1>{t('dashboard.title')}</h1>
            <p>{t('dashboard.signInPrompt')}</p>
            <button className='btn btn-primary' onClick={() => login('/dashboard')}>
              {t('dashboard.signIn')}
            </button>
          </div>
        </div>
      </Layout>
    )
  }

  if (fetchLoading) {
    return (
      <Layout>
        <div className='dashboard'>
          <p className='dashboard-loading'>{t('dashboard.loadingLicenses')}</p>
        </div>
      </Layout>
    )
  }

  if (fetchError) {
    return (
      <Layout>
        <div className='dashboard'>
          <div className='dashboard-error'>
            <h1>{t('dashboard.error')}</h1>
            <p>{fetchError}</p>
          </div>
        </div>
      </Layout>
    )
  }

  if (customers.length === 0) {
    return (
      <Layout>
        <div className='dashboard'>
          <h1>{t('dashboard.title')}</h1>
          <p className='dashboard-empty'>
            {t('dashboard.noSubscriptions')}{' '}
            <a href='https://aws.amazon.com/marketplace' target='_blank' rel='noopener noreferrer'>
              {t('dashboard.subscribeLink')}
            </a>
            {t('dashboard.toGetStarted')}
          </p>
        </div>
      </Layout>
    )
  }

  return (
    <Layout>
      <div className='dashboard'>
        <h1>{t('dashboard.title')}</h1>

        {customers.map(customer => (
          <div key={customer.customerId} className='dashboard-subscription'>
            <div className='dashboard-subscription-header'>
              <span className='dashboard-customer-id'>
                {t('dashboard.subscription', { customerId: customer.customerId })}
              </span>
              <span
                className={`dashboard-status-badge dashboard-status-badge--${customer.subscriptionStatus.toLowerCase()}`}
              >
                {customer.subscriptionStatus}
              </span>
            </div>

            {customer.licenses.length === 0 ? (
              <p className='dashboard-no-licenses'>{t('dashboard.noLicenses')}</p>
            ) : (
              <div className='dashboard-licenses'>
                {customer.licenses.map(license => (
                  <LicenseCard key={license.licenseId} license={license} copiedId={copiedId} onCopy={handleCopy} />
                ))}
              </div>
            )}

            <button
              className='btn btn-secondary dashboard-add-btn'
              disabled={customer.subscriptionStatus !== 'ACTIVE' || addingFor === customer.customerId}
              onClick={() => handleAddDeployment(customer.customerId)}
            >
              {addingFor === customer.customerId ? t('dashboard.adding') : t('dashboard.addDeployment')}
            </button>
          </div>
        ))}

        <div className='dashboard-next-steps'>
          <strong>{t('dashboard.nextSteps')}</strong>
          <ol>
            <li>{t('dashboard.step1')}</li>
            <li>
              {t('dashboard.step2')}{' '}
              <a href='https://console.aws.amazon.com/cloudformation' target='_blank' rel='noopener noreferrer'>
                {t('dashboard.step2Link')}
              </a>
              .
            </li>
            <li>
              {t('dashboard.step3Prefix')} <strong>{t('dashboard.step3Param')}</strong> {t('dashboard.step3Suffix')}
            </li>
            <li>{t('dashboard.step4')}</li>
          </ol>
        </div>
      </div>
    </Layout>
  )
}

function LicenseCard({
  license,
  copiedId,
  onCopy
}: {
  license: LicenseItem
  copiedId: string | null
  onCopy: (key: string, id: string) => Promise<void>
}) {
  const { t } = useTranslation()
  return (
    <div className='dashboard-license-card'>
      <div className='dashboard-license-header'>
        <span className='dashboard-license-id'>{t('dashboard.licenseId', { id: license.licenseId.slice(0, 8) })}</span>
        <span className={`dashboard-status-badge dashboard-status-badge--${license.status.toLowerCase()}`}>
          {license.status}
        </span>
      </div>

      <div className='registration-key-box'>
        <code>{license.licenseKey}</code>
      </div>

      <button
        className='btn btn-primary registration-copy-btn'
        onClick={() => onCopy(license.licenseKey, license.licenseId)}
      >
        {copiedId === license.licenseId ? t('dashboard.copied') : t('dashboard.copyKey')}
      </button>

      <div className='dashboard-license-meta'>
        {license.boundAccountId && (
          <span>
            {t('dashboard.boundTo')} <strong>{license.boundAccountId}</strong> / <strong>{license.boundRegion}</strong>
          </span>
        )}
        <span>
          {t('dashboard.issued')} {new Date(license.issuedAt).toLocaleDateString()}
        </span>
        {license.activatedAt && (
          <span>
            {t('dashboard.activated')} {new Date(license.activatedAt).toLocaleDateString()}
          </span>
        )}
      </div>
    </div>
  )
}

export default DashboardPage
